import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import guides from '../data/guides';
import './Landing.css';

function Landing() {
  const navigate = useNavigate();
  const canvasRef = useRef(null);

  const phrases = ['built in', 'record-ready', 'simple', 'connected'];
  const [displayText, setDisplayText] = useState(phrases[0]);
  const phraseRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const TYPE_SPEED = 70;
    const DELETE_SPEED = 40;
    const PAUSE = 2200;

    const cycle = () => {
      if (cancelled) return;
      const current = phrases[phraseRef.current];
      let i = current.length;

      const deleteChar = () => {
        if (cancelled) return;
        if (i > 0) {
          i--;
          setDisplayText(current.slice(0, i));
          setTimeout(deleteChar, DELETE_SPEED);
        } else {
          phraseRef.current = (phraseRef.current + 1) % phrases.length;
          const next = phrases[phraseRef.current];
          let j = 0;
          const typeChar = () => {
            if (cancelled) return;
            if (j <= next.length) {
              setDisplayText(next.slice(0, j));
              j++;
              setTimeout(typeChar, TYPE_SPEED);
            } else {
              setTimeout(cycle, PAUSE);
            }
          };
          setTimeout(typeChar, 150);
        }
      };

      deleteChar();
    };

    const timeout = setTimeout(cycle, PAUSE);
    return () => { cancelled = true; clearTimeout(timeout); };
  }, []);

  // Scroll fade-in observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.15 }
    );
    document.querySelectorAll('.landing .fade-up').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Canvas grid — deforms toward cursor like pushing into fabric
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const SPACING = 40;
    const RADIUS = 220;
    const STRENGTH = 38;
    const DOT_SIZE = 1.2;
    const COLOR = '148, 163, 184';

    let mouseX = -1000, mouseY = -1000;
    let curMX = -1000, curMY = -1000;
    let rafId;
    let cols, rows;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = document.documentElement.scrollHeight;
      cols = Math.ceil(canvas.width / SPACING) + 1;
      rows = Math.ceil(canvas.height / SPACING) + 1;
    };

    const handleMouse = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY + window.scrollY;
    };

    const draw = () => {
      curMX += (mouseX - curMX) * 0.08;
      curMY += (mouseY - curMY) * 0.08;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const baseX = c * SPACING;
          const baseY = r * SPACING;

          const dx = baseX - curMX;
          const dy = baseY - curMY;
          const dist = Math.sqrt(dx * dx + dy * dy);

          let x = baseX;
          let y = baseY;

          if (dist < RADIUS) {
            const force = (1 - dist / RADIUS);
            const push = force * force * STRENGTH;
            x += (dx / dist) * push;
            y += (dy / dist) * push;
          }

          const proximity = Math.max(0, 1 - dist / (RADIUS * 1.5));
          const alpha = 0.08 + proximity * 0.25;

          ctx.beginPath();
          ctx.arc(x, y, DOT_SIZE + proximity * 0.8, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${COLOR}, ${alpha})`;
          ctx.fill();
        }
      }

      rafId = requestAnimationFrame(draw);
    };

    resize();
    window.addEventListener('mousemove', handleMouse);
    window.addEventListener('resize', resize);
    rafId = requestAnimationFrame(draw);

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(document.documentElement);

    return () => {
      window.removeEventListener('mousemove', handleMouse);
      window.removeEventListener('resize', resize);
      resizeObserver.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, []);

  // 3D tilt on scroll for cards
  useEffect(() => {
    const handleScroll = () => {
      document.querySelectorAll('.landing .tilt-on-scroll').forEach((el) => {
        const rect = el.getBoundingClientRect();
        const viewH = window.innerHeight;
        const center = rect.top + rect.height / 2;
        const offset = (center - viewH / 2) / viewH;
        const rotateX = offset * -5;
        const translateZ = Math.max(0, (1 - Math.abs(offset) * 2)) * 10;
        el.style.transform = `perspective(800px) rotateX(${rotateX}deg) translateZ(${translateZ}px)`;
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // guides imported from ../data/guides

  const steps = [
    { title: 'Create the document', desc: 'Choose the CemSites record type, add fields, and prepare the document your team needs.' },
    { title: 'Publish and place it', desc: 'Publish the document, open the matching CemSites record, and place the document button there.' },
    { title: 'Preview, print, or sign', desc: 'Once placed, use the document button from the record just like the rest of your CemSites workflow.' },
  ];

  return (
    <div className="landing">
      <canvas className="lgrid-canvas" ref={canvasRef} />

      {/* Nav */}
      <nav className="lnav">
        <div className="lnav-brand">
          <img src="/logo.png" alt="CemSites" className="lnav-logo-img" />
          <span className="lnav-divider" />
          <span className="lnav-product">Doc Builder</span>
        </div>
        <ul className="lnav-links">
          <li><a href="#guides">Guides</a></li>
          <li><a href="#how-it-works">How It Works</a></li>
          <li><a href="#support">Support</a></li>
        </ul>
        <button className="lbtn lbtn-outline lbtn-sm" onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
      </nav>

      {/* Hero */}
      <section className="lhero">
        <div className="lhero-content">
          <h1>Documents that are</h1>
          <div className="ltype-line"><span className="ltype-text">{displayText}<span className="ltype-cursor" /></span></div>
          <p>Learn how to create, publish, place, preview, print, and sign documents from the CemSites records your team already uses.</p>
          <div className="lhero-buttons">
            <a href="#guides" className="lbtn lbtn-primary">Browse Guides</a>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="lsection lsection-alt">
        <div className="lcontainer" style={{ textAlign: 'center' }}>
          <div className="fade-up">
            <div className="lsection-label">Quick Overview</div>
            <h2 className="lsection-title">How it works</h2>
            <p className="lsection-sub" style={{ marginInline: 'auto' }}>Create, publish, place, then use the document from the matching CemSites record.</p>
          </div>
          <div className="lsteps fade-up">
            {steps.map((s, i) => (
              <div className="lstep tilt-on-scroll" key={i}>
                <div className="lstep-num">{i + 1}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Guides */}
      <section id="guides" className="lsection">
        <div className="lcontainer">
          <div className="fade-up">
            <div className="lsection-label">Guides & How-Tos</div>
            <h2 className="lsection-title">Learn Doc Builder</h2>
            <p className="lsection-sub">Step-by-step walkthroughs for every feature.</p>
          </div>
          <div className="lfeatures-grid">
            {guides.map((g) => (
              <Link to={`/guide/${g.id}`} className="lfeature-card fade-up tilt-on-scroll" key={g.id}>
                <div className="lguide-tag">{g.tag}</div>
                <h3>{g.title}</h3>
                <p>{g.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CemSites Integration */}
      <section className="lsection lsection-alt">
        <div className="lcontainer">
          <div className="lintegration fade-up">
            <div className="lintegration-text">
              <div className="lsection-label">Built Into CemSites</div>
              <h2 className="lsection-title">Works where you already work</h2>
              <p className="lsection-sub" style={{ marginBottom: '1.5rem' }}>Doc Builder lives inside CemSites and follows your normal record workflow.</p>
              <ul className="lintegration-list">
                <li>Create documents for the record types available on your site</li>
                <li>Publish documents before placing them in CemSites</li>
                <li>Place documents from the matching record page</li>
                <li>Preview, print, or sign documents from the placed button</li>
              </ul>
            </div>
            <div className="lintegration-visual tilt-on-scroll">
              <div className="lmock-window">
                <div className="lmock-titlebar">
                  <div className="lmock-dots">
                    <span /><span /><span />
                  </div>
                  <div className="lmock-title">CemSites — Record Page</div>
                </div>
                <div className="lmock-body">
                  <div className="lmock-row lmock-header-row">
                    <div className="lmock-name">Work Order #1042</div>
                    <div className="lmock-badge">Active</div>
                  </div>
                  <div className="lmock-row">
                    <div className="lmock-label">Record Type</div>
                    <div className="lmock-value">Work Order</div>
                  </div>
                  <div className="lmock-row">
                    <div className="lmock-label">Document</div>
                    <div className="lmock-value">Service Form</div>
                  </div>
                  <div className="lmock-divider" />
                  <div className="lmock-btn-row">
                    <div className="lmock-action-btn">Preview Document</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support */}
      <section id="support" className="lsection">
        <div className="lcontainer" style={{ textAlign: 'center' }}>
          <div className="fade-up">
            <div className="lsection-label">Need Help?</div>
            <h2 className="lsection-title">We're here for you</h2>
            <p className="lsection-sub" style={{ marginInline: 'auto' }}>Having trouble or have a question? Reach out to the CemSites support team.</p>
            <div className="lsupport-actions">
              <a href="mailto:support@cemsites.com" className="lbtn lbtn-primary">Email Support</a>
              <a href="tel:5551234567" className="lbtn lbtn-outline">Call (555) 123-4567</a>
            </div>
          </div>
        </div>
      </section>

      <footer className="lfooter">
        <p>&copy; 2026 CemSites. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default Landing;
